import { DomainError } from "./errors";
import { todayInSaoPaulo } from "./dates";

/**
 * Regras puras da integração com o Mercado Livre (T1.16, RF-VEN-09). Tudo em inteiros: valores do ML chegam como
 * número decimal do JSON e viram centavos pela representação, nunca por `n * 100`. Os tetos, o lote de sincronização,
 * o debounce, a trava do token e o estoque enviado valem também para a Shopee (T1.17).
 */

/** Contas conectadas por empresa e por marketplace. */
export const MAX_ML_ACCOUNTS = 5;
/** Anúncios (anúncio × variação) importados por conta. */
export const MAX_ML_LISTINGS = 5_000;
/** Pedidos por página da busca do polling e páginas por rodada. */
export const ML_POLL_PAGE = 50;
export const ML_POLL_MAX_PAGES = 2;
/** Volta do polling sobre o último horário visto (atraso do índice de busca do ML). */
export const ML_POLL_OVERLAP_MS = 15 * 60_000;
/** Itens por pedido importado; acima disso o pedido fica para revisão sem pedido de venda. */
export const MAX_ML_ORDER_ITEMS = 200;
/** Itens (anúncios) por lote de sincronização. */
export const ML_SYNC_BATCH_ITEMS = 20;
/** Atraso do evento de sincronização (agrupa mudanças seguidas de estoque). */
export const ML_SYNC_DEBOUNCE_S = 20;
/** Estoque máximo enviado a um anúncio. */
export const ML_MAX_STOCK = 99_999;
/** Renova o token quando faltar menos que isso. */
export const ML_TOKEN_REFRESH_MARGIN_MS = 5 * 60_000;
/** Trava de renovação do token (uma por conta). */
export const ML_REFRESH_LOCK_MS = 60_000;
/** Teto de centavos aceito do ML (R$ 100 milhões por valor). */
export const ML_MAX_AMOUNT_CENTS = 10_000_000_000;

const AMOUNT_RE = /^(\d{1,10})(?:\.(\d{1,2}))?$/;

/**
 * Valor decimal de um marketplace (número JSON ou texto) em centavos pela representação; até 2 casas decimais.
 * `source` entra na mensagem ("do Mercado Livre", "da Shopee").
 */
export function decimalAmountCents(v: number | string, source: string): number {
  const s = typeof v === "number" ? (Number.isFinite(v) ? String(v) : "") : typeof v === "string" ? v : "";
  const m = AMOUNT_RE.exec(s);
  if (!m) throw new DomainError("invalid_amount", `Valor inválido ${source}: ${String(v)}`);
  const cents = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  if (cents > ML_MAX_AMOUNT_CENTS)
    throw new DomainError("invalid_amount", `Valor ${source} acima do limite: ${String(v)}`);
  return cents;
}

/** Valor do ML (número JSON ou texto) em centavos; aceita até 2 casas decimais. */
export const mlAmountCents = (v: number | string): number => decimalAmountCents(v, "do Mercado Livre");

/** Disponível (q4) → quantidade inteira enviada ao anúncio; null (kit sem componente controlado) = teto. */
export function mlStockQty(availableQ4: number | null): number {
  if (availableQ4 === null) return ML_MAX_STOCK;
  if (availableQ4 <= 0) return 0;
  return Math.min(ML_MAX_STOCK, Math.floor(availableQ4 / 10_000));
}

export type MlOrderAction = "import" | "wait" | "cancel" | "review";
/** O que fazer com um pedido importado (qualquer marketplace). */
export type MarketplaceOrderAction = MlOrderAction;

const WAITING = new Set(["confirmed", "payment_required", "payment_in_process", "partially_paid"]);

/** O que fazer com o pedido conforme a situação no ML (padrão de produto: só pago vira pedido de venda). */
export function mlOrderAction(status: string): MlOrderAction {
  if (status === "paid") return "import";
  if (WAITING.has(status)) return "wait";
  if (status === "cancelled" || status === "invalid") return "cancel";
  return "review";
}

export const ML_ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  confirmed: "Confirmado (aguardando pagamento)",
  payment_required: "Aguardando pagamento",
  payment_in_process: "Pagamento em processamento",
  partially_paid: "Pago parcialmente",
  paid: "Pago",
  partially_refunded: "Reembolsado parcialmente",
  pending_cancel: "Cancelamento pendente",
  cancelled: "Cancelado",
  invalid: "Inválido",
};

/** Data (AAAA-MM-DD) do pedido em São Paulo. */
export function mlOrderDate(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) throw new DomainError("invalid_date", `Data inválida do Mercado Livre: ${iso}`);
  return todayInSaoPaulo(new Date(ms));
}

const RESOURCE_RE: Readonly<Record<string, RegExp>> = {
  orders_v2: /^\/orders\/(\d{1,20})$/,
  items: /^\/items\/(MLB\d{1,15})$/,
};

/** Id do recurso de uma notificação, só para os tópicos tratados e no formato exato (o resto é ignorado). */
export function mlResourceId(topic: string, resource: string): string | null {
  const re = Object.hasOwn(RESOURCE_RE, topic) ? RESOURCE_RE[topic]! : null;
  return re?.exec(resource)?.[1] ?? null;
}

export type ItemPriceTarget = { kind: "none" } | { kind: "price"; priceCents: number } | { kind: "conflict" };

/** O ML exige o mesmo preço em todas as variações do anúncio: preços diferentes = conflito, nada é enviado. */
export function itemPriceTarget(prices: readonly number[]): ItemPriceTarget {
  if (prices.length === 0) return { kind: "none" };
  const first = prices[0]!;
  return prices.every((p) => p === first) ? { kind: "price", priceCents: first } : { kind: "conflict" };
}

/** Espera antes de tentar de novo a sincronização (429/5xx): 30 s × 2^tentativa, ou o Retry-After, até 15 min. */
export function mlSyncBackoffS(attempts: number, retryAfterS = 0): number {
  return Math.min(900, Math.max(retryAfterS, 30 * 2 ** Math.min(attempts, 10)));
}

/** Token ainda serve (não vence dentro da margem de renovação). */
export function isMlTokenFresh(expiresAt: string | null, now: number = Date.now()): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  return !Number.isNaN(ms) && ms - now > ML_TOKEN_REFRESH_MARGIN_MS;
}
